<template>
  <div class="consultation-page">
    <!-- Quotation upload dialog -->
    <v-dialog v-model="showQuotation" width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Envoyer un devis (pdf)
        </v-card-title>

        <v-card-text class="mt-5">
          <v-file-input
            @change="quotationFile = $event"
            v-validate="'mimes:application/pdf'"
            data-vv-as="quotation"
            :error-messages="errors.collect('quotation')"
            name="quotation"
            accept="application/pdf"
            label="Choisir un devis"
          ></v-file-input>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="uploadQuotation">
            Envoyer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Snackbar for user messages -->
    <v-snackbar v-model="snackbarShow" :color="snackbarColor" right top>
      {{ snackbarText }}
      <v-btn dark text @click="snackbarShow = false">Fermer</v-btn>
    </v-snackbar>

    <template v-if="!loading">
      <ConsultationHeader :project.sync="project" />

      <v-tabs v-model="tab">
        <v-tab @click="currentTab = 'General'">General</v-tab>
        <v-tab @click="currentTab = 'Gestion'">Gestion</v-tab>
        <v-tab @click="currentTab = 'Medias'">Medias</v-tab>
        <v-tab @click="currentTab = 'Marge'">Marge</v-tab>

        <v-tabs-items v-model="tab" class="pa-4">
          <v-tab-item
            :value="tab"
            :transition="false"
            :reverse-transition="false"
          >
            <keep-alive>
              <component
                :is="currentTabComponent"
                v-bind.sync="currentTabProps"
              ></component>
            </keep-alive>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>

      <v-layout
        justify-space-between
        align-center
        class="btn-container mt-4"
        wrap
      >
        <v-btn @click="close">Fermer</v-btn>
        <v-btn @click="update">Enregistrer</v-btn>
        <v-btn @click="printProjectAndMedias"
          >Impression de la fiche et des médias</v-btn
        >
        <v-btn @click="printMedias">Imprimer les médias seuls</v-btn>
        <v-btn @click="printProject">Imprimer La fiche seule</v-btn>
        <v-btn @click="printNotice" :loading="loading"
          >Imprimer la notice</v-btn
        >

        <v-btn @click="showQuotation = true">Envoyer devis</v-btn>
      </v-layout>
    </template>
  </div>
</template>

<script>
  import { mapActions, mapState } from "vuex";

  // Repositories
  import PrintRepo from "@/repositories/print";

  const ConsultationHeader = () => import("./components/ConsultationHeader");
  const GeneralTab = () => import("./components/GeneralTab");
  const GestionTab = () => import("./components/GestionTab");
  const MediasTab = () => import("./components/MediasTab");
  const MargeTab = () => import("./components/MargeTab");

  export default {
    name: "ConsultationPage",
    components: {
      ConsultationHeader,
      GeneralTab,
      GestionTab,
      MediasTab,
      MargeTab
    },
    data() {
      return {
        loading: false,

        snackbarShow: false,
        snackbarColor: "success",
        snackbarText: "",

        showQuotation: false,
        quotationFile: null,

        tab: null,
        tabs: ["General", "Gestion", "Medias", "Marge"],
        currentTab: "General",

        project: null,
        articles: []
      };
    },
    computed: {
      ...mapState({
        // Settings
        devices: state => state.settings.devices,
        operations: state => state.settings.operations,
        brackets: state => state.settings.brackets,
        agencies: state => state.settings.agencies,
        technicians: state => state.settings.technicians
      }),

      // Current tab
      currentTabComponent() {
        return this.currentTab + "Tab";
      },

      currentTabProps() {
        return {
          project: this.project,

          // Settings
          devices: this.devices,
          operations: this.operations,
          brackets: this.brackets,
          agencies: this.agencies,
          technicians: this.technicians
        };
      }
    },
    methods: {
      ...mapActions({
        getProject: "projects/getProject",
        updateProject: "projects/updateProject",
        fetchCalculation: "projects/fetchCalculation",

        // Settings
        fetchDevicesSettings: "settings/fetchDevicesSettings",
        fetchOperationsSettings: "settings/fetchOperationsSettings",
        fetchBracketsSettings: "settings/fetchBracketsSettings",
        fetchAgenciesSettings: "settings/fetchAgenciesSettings",
        fetchTechniciansSettings: "settings/fetchTechniciansSettings"
      }),

      /**
       * Upload quotation file to server
       */
      async uploadQuotation() {
        if (!this.quotationFile) return;

        const isValid = await this.$validator.validate();
        console.log(isValid);

        if (!isValid) return;

        try {
          let data = new FormData();
          data.append("invoice_pdf", this.quotationFile);

          await PrintRepo.uploadQuotation(
            this.$route.params.project_uuid,
            data
          );

          this.snackbarColor = "success";
          this.snackbarText = "Envoi réussie";
          this.snackbarShow = true;
          this.quotationFile = null;
          this.showQuotation = false;
        } catch (error) {
          this.snackbarText = error.message;

          if (error.response.status == 422) {
            this.snackbarText = "Format invalide. Seul les PDF sont autorisés";
          }

          this.snackbarColor = "error";
          this.snackbarShow = true;
        }
      },

      // Print project
      async printProject() {
        try {
          const project_uuid = this.$route.params.project_uuid;
          const res = await PrintRepo.get(project_uuid);
          let blob = new Blob([res], { type: "application/pdf" });

          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          link.download =
            this.project.patient.lastname +
            "_" +
            this.project.patient.firstname +
            ".pdf";
          link.click();
        } catch (error) {
          console.error(error);
        }
      },

      // Print project and medias
      async printProjectAndMedias() {
        try {
          const project_uuid = this.$route.params.project_uuid;
          const res = await PrintRepo.getProjectAndMedias(project_uuid);
          let blob = new Blob([res], { type: "application/pdf" });

          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          link.download =
            this.project.patient.lastname +
            "_" +
            this.project.patient.firstname +
            ".pdf";
          link.click();
        } catch (error) {
          console.error(error);
        }
      },

      // Print medias
      async printMedias() {
        try {
          const project_uuid = this.$route.params.project_uuid;
          const res = await PrintRepo.getMedias(project_uuid);
          let blob = new Blob([res], { type: "application/pdf" });

          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          link.download =
            this.project.patient.lastname +
            "_" +
            this.project.patient.firstname +
            ".pdf";
          link.click();
        } catch (error) {
          console.error(error);
        }
      },

      /**
       * Print notice
       */
      async printNotice() {
        try {
          this.loading = true;
          const project_uuid = this.$route.params.project_uuid;
          const res = await PrintRepo.getNoticePdf(project_uuid);
          let blob = new Blob([res], { type: "application/pdf" });

          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          this.loading = false;

          link.download =
            this.project.patient.lastname +
            "_" +
            this.project.patient.firstname +
            ".pdf";
          link.click();
        } catch (error) {
          this.loading = false;
          console.error(error);
        }
      },

      // Update project
      async update() {
        try {
          // Qick validation
          // TODO: Improve that shit
          if (this.project.center && this.project.center.length > 50) {
            this.snackbarText = "Maximum 50 caractères pour le champs centre";
            this.snackbarColor = "error";
            this.snackbarShow = true;
            return;
          }

          const project_uuid = this.$route.params.project_uuid;
          await this.updateProject({
            project_uuid: project_uuid,
            payload: this.project
          });
          await this.fetchCalculation(project_uuid);
          this.init();

          this.snackbarText = "Enregistrement effectué";
          this.snackbarColor = "success";
          this.snackbarShow = true;
        } catch (error) {
          this.snackbarText = error;
          this.snackbarColor = "error";
          this.snackbarShow = true;
        }
      },

      // Update project
      close() {
        this.$router.push("/");
      },

      // Load data
      async init() {
        try {
          this.loading = true;
          const project_uuid = this.$route.params.project_uuid;
          await this.fetchCalculation(project_uuid);

          this.project = await this.getProject(project_uuid);

          // Settings
          await this.fetchDevicesSettings(this.project.product_model);
          await this.fetchOperationsSettings();
          await this.fetchBracketsSettings();
          await this.fetchAgenciesSettings();
          await this.fetchTechniciansSettings();
          // await this.fetchCalculation(project_uuid);
          this.loading = false;
        } catch (error) {
          console.error(error);
        }
      }
    },
    created() {
      this.init();
    }
  };
</script>

<style lang="scss" scoped>
  .consultation-page {
    .btn-container {
      .v-btn {
        background-color: #880e4f !important;
        color: #fff;
        margin-bottom: 10px;
      }
    }
    .v-window {
      overflow-y: auto;
    }
  }
</style>
